import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { UserPlus, Check, X, AlertCircle, Loader2 } from 'lucide-react';
import Button from '../components/common/Button';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

interface InvitationDetails {
  senderEmail: string;
  senderName: string;
  receiverEmail: string;
  expiresAt: string;
}

export default function AcceptInvitation() {
  const { t, i18n } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { isAuthenticated, user, setUser } = useAuthStore();

  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Charger les détails de l'invitation
  useEffect(() => {
    if (!token) {
      setError(t('acceptInvitation.tokenMissing'));
      setIsLoading(false);
      return;
    }

    const fetchInvitation = async () => {
      try {
        const response = await api.get(`/partner/invitation/${token}`);
        if (response.data.success) {
          setInvitation(response.data.data);
        }
      } catch (err: any) {
        const message = err.response?.data?.message || t('acceptInvitation.notFoundOrExpired');
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;

    setIsAccepting(true);
    setError('');

    try {
      const response = await api.post('/partner/accept-invitation', { token });
      if (response.data.success) {
        // Mettre à jour le profil utilisateur dans le store
        if (response.data.data) {
          setUser(response.data.data);
        }
        setSuccess(true);
        // Rediriger vers le dashboard après 2 secondes
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('acceptInvitation.acceptError'));
    } finally {
      setIsAccepting(false);
    }
  };

  // Si l'utilisateur n'est pas connecté, rediriger vers login
  const handleLoginRedirect = () => {
    navigate(`/login?returnUrl=${encodeURIComponent(`/accept-invitation?token=${token}`)}`);
  };

  // Affichage du chargement
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('acceptInvitation.loading')}</p>
        </div>
      </div>
    );
  }

  // Affichage de l'erreur
  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t('acceptInvitation.invalidTitle')}
            </h1>

            <p className="text-gray-600 mb-6">
              {error}
            </p>

            <Button
              variant="primary"
              className="w-full"
              onClick={() => navigate('/')}
            >
              {t('backToHome', { ns: 'common' })}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Affichage du succès
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Link to="/" className="text-3xl font-bold text-primary-600">
              Splinity
            </Link>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t('acceptInvitation.successTitle')}
            </h1>

            <p className="text-gray-600 mb-6">
              <Trans t={t} i18nKey="acceptInvitation.successMessage" values={{ name: invitation?.senderName }} components={{ strong: <strong /> }} />
            </p>

          </div>
        </div>
      </div>
    );
  }

  // Affichage principal : invitation valide
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="text-3xl font-bold text-primary-600">
            Splinity
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <UserPlus className="w-8 h-8 text-primary-600" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            {t('acceptInvitation.title')}
          </h1>

          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
            <p className="text-primary-900">
              <Trans t={t} i18nKey="acceptInvitation.inviteMessage" values={{ name: invitation?.senderName }} components={{ strong: <strong /> }} />
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 mb-2">
              <strong>{t('acceptInvitation.whatHappens')}</strong>
            </p>
            <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
              <li>{t('acceptInvitation.joinSpace', { name: invitation?.senderName })}</li>
              <li>{t('acceptInvitation.viewBudget')}</li>
              <li>{t('acceptInvitation.incomeConsidered')}</li>
            </ul>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {isAuthenticated && user ? (
            // Utilisateur connecté : montrer le bouton accepter
            <div className="space-y-3">
              <p className="text-sm text-gray-600 text-center mb-4">
                <Trans t={t} i18nKey="acceptInvitation.connectedAs" values={{ email: user.email }} components={{ strong: <strong /> }} />
              </p>


              <Button
                variant="primary"
                className="w-full"
                size="lg"
                leftIcon={<Check className="w-5 h-5" />}
                onClick={handleAccept}
                isLoading={isAccepting}
              >
                {t('acceptInvitation.acceptButton')}
              </Button>

              <Button
                variant="secondary"
                className="w-full"
                leftIcon={<X className="w-5 h-5" />}
                onClick={() => navigate('/dashboard')}
                disabled={isAccepting}
              >
                {t('acceptInvitation.declineButton')}
              </Button>
            </div>
          ) : (
            // Utilisateur non connecté : proposer de se connecter
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                {t('acceptInvitation.loginPrompt')}
              </p>

              <Button
                variant="primary"
                className="w-full"
                size="lg"
                onClick={handleLoginRedirect}
              >
                {t('login.submit')}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                {t('acceptInvitation.noAccount')}{' '}
                <Link
                  to={`/register?invitation=${token}`}
                  className="text-primary-600 hover:underline"
                >
                  {t('acceptInvitation.createAccount')}
                </Link>
              </p>
            </div>
          )}

          <p className="mt-6 text-xs text-gray-500 text-center">
            {t('acceptInvitation.expiresOn')}{' '}
            {invitation?.expiresAt && new Date(invitation.expiresAt).toLocaleDateString(i18n.language, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </div>

        <p className="mt-8 text-center text-sm text-gray-500">
          <Link to="/" className="hover:text-primary-600">
            &larr; {t('backToHome', { ns: 'common' })}
          </Link>
        </p>
      </div>
    </div>
  );
}
